import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Plug, BrainCircuit, ShoppingBag, ArrowRight, CheckCircle2 } from "lucide-react";

const STEPS = [
  {
    n: "01",
    icon: Plug,
    title: "Connect POS in 3 Minutes",
    body: "One-click OAuth integration with Lightspeed Retail, Square Terminal, or upload historical sales via CSV / Excel. Data is encrypted and synced automatically.",
    badge: "Zero IT required",
  },
  {
    n: "02",
    icon: BrainCircuit,
    title: "Optics Forecasts Demand",
    body: "Our engine maps your sales trends against live OpenWeather forecasts, public holidays, and local events to predict exact item quantities for every product on your menu.",
    badge: "Updated every morning",
  },
  {
    n: "03",
    icon: ShoppingBag,
    title: "Prep & Order with Confidence",
    body: "Review smart order recommendations with plain-English explanations. Accept with 1 click to dispatch purchase orders directly to your suppliers.",
    badge: "Zero stockouts",
  },
];

export default function HowItWorks() {
  return (
    <section id="how-it-works" className="max-w-6xl mx-auto px-6 py-24 md:py-32 scroll-mt-16">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.5 }}
        className="text-center max-w-2xl mx-auto"
      >
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-3.5 py-1.5 rounded-full">
          Simple 3-Step Flow
        </div>
        <h2 className="font-display font-semibold text-3xl md:text-4xl text-zinc-900 mt-4">
          From historical POS data to tomorrow's prep sheet in minutes
        </h2>
        <p className="text-zinc-500 mt-3 text-base">
          No complex data engineering or manual spreadsheets. Optics works seamlessly in the background.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
        {STEPS.map((s, i) => (
          <motion.div
            key={s.n}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.45, delay: i * 0.1 }}
            className="bg-white border border-zinc-200/90 rounded-2xl p-8 shadow-card flex flex-col justify-between relative group hover:border-zinc-300 hover:shadow-lg transition-all"
          >
            <div>
              <div className="flex items-center justify-between">
                <div className="h-12 w-12 rounded-xl bg-zinc-900 grid place-items-center text-amber-400 group-hover:scale-105 transition-transform">
                  <s.icon className="h-6 w-6" />
                </div>
                <span className="font-mono text-2xl font-bold text-zinc-200 group-hover:text-zinc-400 transition-colors">
                  {s.n}
                </span>
              </div>

              <h3 className="font-display font-semibold text-lg text-zinc-900 mt-6">{s.title}</h3>
              <p className="text-xs text-zinc-500 mt-2.5 leading-relaxed">{s.body}</p>
            </div>

            <div className="mt-8 pt-4 border-t border-zinc-100 flex items-center gap-2 text-xs font-semibold text-emerald-700">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span>{s.badge}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
